import { supabase } from '../lib/supabase';

export const recurringTransactionService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('recurring_transactions')?.select(`
          id,
          user_id,
          account_id,
          category_id,
          transaction_name,
          description,
          amount,
          type,
          frequency,
          interval_count,
          start_date,
          end_date,
          next_execution_date,
          last_execution_date,
          status,
          email_notification,
          monthly_overflow_rule,
          semi_monthly_rule,
          color,
          created_at,
          updated_at,
          account:accounts(id, name, account_group),
          category:categories(id, name, color, icon, type, parent_id)
        `)?.order('next_execution_date', { ascending: true });

      if (filters?.type) {
        query = query?.eq('type', filters?.type);
      }
      if (filters?.frequency) {
        query = query?.eq('frequency', filters?.frequency);
      }
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      const transactions = data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        accountId: row?.account_id,
        account: row?.account?.name || '',
        categoryId: row?.category_id,
        category: row?.category?.name || '',
        categoryColor: row?.category?.color || '#6366f1',
        categoryIcon: row?.category?.icon || 'Tag',
        categoryType: row?.category?.type || '',
        categoryParentId: row?.category?.parent_id || null,
        transactionName: row?.transaction_name,
        description: row?.description,
        amount: parseFloat(row?.amount),
        type: row?.type,
        frequency: row?.frequency,
        intervalCount: row?.interval_count,
        startDate: row?.start_date,
        endDate: row?.end_date,
        nextExecutionDate: row?.next_execution_date,
        lastExecutionDate: row?.last_execution_date,
        status: row?.status,
        emailNotification: row?.email_notification,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      }));

      // Sort by Type, Category, and Subcategory (matching rest of app)
      return transactions?.sort((a, b) => {
        // First sort by type
        if (a?.type !== b?.type) {
          return a?.type?.localeCompare(b?.type);
        }
        
        // Then by category name
        const categoryCompare = (a?.category || '')?.localeCompare(b?.category || '');
        if (categoryCompare !== 0) {
          return categoryCompare;
        }
        
        // Then by subcategory (parent_id indicates it's a subcategory)
        // Items with parent_id (subcategories) come after their parent
        if (a?.categoryParentId !== b?.categoryParentId) {
          if (!a?.categoryParentId) return -1;
          if (!b?.categoryParentId) return 1;
        }
        
        // Finally by next execution date
        return new Date(a?.nextExecutionDate) - new Date(b?.nextExecutionDate);
      });
    } catch (error) {
      console.error('Error fetching recurring transactions:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('recurring_transactions')?.select(`
          id,
          user_id,
          account_id,
          category_id,
          transaction_name,
          description,
          amount,
          type,
          frequency,
          interval_count,
          start_date,
          end_date,
          next_execution_date,
          last_execution_date,
          status,
          email_notification,
          monthly_overflow_rule,
          semi_monthly_rule,
          color,
          created_at,
          updated_at,
          account:accounts(id, name, account_group),
          category:categories(id, name, color, icon)
        `)?.eq('id', id)?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        userId: data?.user_id,
        accountId: data?.account_id,
        account: data?.account?.name || '',categoryId: data?.category_id,category: data?.category?.name || '',categoryColor: data?.category?.color || '#6366f1',categoryIcon: data?.category?.icon || 'Tag',
        transactionName: data?.transaction_name,
        description: data?.description,
        amount: parseFloat(data?.amount),
        type: data?.type,
        frequency: data?.frequency,
        intervalCount: data?.interval_count,
        startDate: data?.start_date,
        endDate: data?.end_date,
        nextExecutionDate: data?.next_execution_date,
        lastExecutionDate: data?.last_execution_date,
        status: data?.status,
        emailNotification: data?.email_notification,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching recurring transaction:', error);
      throw error;
    }
  },

  async create(data) {
    const { data: result, error } = await supabase?.from('recurring_transactions')?.insert([{
        user_id: data?.userId,
        account_id: data?.accountId || null,
        category_id: data?.categoryId || null,
        transaction_name: data?.transactionName,
        description: data?.description || null,
        amount: data?.amount,
        type: data?.type,
        frequency: data?.frequency,
        interval_count: data?.intervalCount || 1,
        start_date: data?.startDate,
        end_date: data?.endDate || null,
        next_execution_date: data?.nextExecutionDate || data?.startDate,
        status: data?.status || 'active',
        email_notification: data?.emailNotification || false,
        monthly_overflow_rule: data?.monthlyOverflowRule || 'last_day',
        semi_monthly_rule: data?.semiMonthlyRule || '1st_and_15th'
      }])?.select()?.single();

    if (error) throw error;
    return this.toCamelCase(result);
  },

  async update(id, data) {
    const updateData = {
      transaction_name: data?.transactionName,
      description: data?.description,
      amount: data?.amount,
      type: data?.type,
      frequency: data?.frequency,
      next_execution_date: data?.nextExecutionDate,
      status: data?.status,
      updated_at: new Date()?.toISOString()
    };

    // Only include optional fields if they're provided
    if (data?.accountId !== undefined) updateData.account_id = data?.accountId || null;
    if (data?.categoryId !== undefined) updateData.category_id = data?.categoryId || null;
    if (data?.emailNotification !== undefined) updateData.email_notification = data?.emailNotification;
    if (data?.monthlyOverflowRule !== undefined) updateData.monthly_overflow_rule = data?.monthlyOverflowRule;
    if (data?.semiMonthlyRule !== undefined) updateData.semi_monthly_rule = data?.semiMonthlyRule;

    const { data: result, error } = await supabase?.from('recurring_transactions')?.update(updateData)?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return this.toCamelCase(result);
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('recurring_transactions')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting recurring transaction:', error);
      throw error;
    }
  },

  async getUpcoming(userId, daysAhead = 30) {
    try {
      const { data, error } = await supabase?.rpc('get_upcoming_recurring_transactions', {
          p_user_id: userId,
          p_days_ahead: daysAhead
        });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        transactionName: row?.transaction_name,
        amount: parseFloat(row?.amount),
        type: row?.type,
        frequency: row?.frequency,
        nextExecutionDate: row?.next_execution_date,
        accountName: row?.account_name,
        categoryName: row?.category_name
      }));
    } catch (error) {
      console.error('Error fetching upcoming recurring transactions:', error);
      throw error;
    }
  },

  toCamelCase(data) {
    if (!data) return null;
    if (Array.isArray(data)) return data?.map(item => this.toCamelCase(item));
    
    return {
      id: data?.id,
      userId: data?.user_id,
      accountId: data?.account_id,
      categoryId: data?.category_id,
      transactionName: data?.transaction_name,
      description: data?.description,
      amount: data?.amount,
      type: data?.type,
      frequency: data?.frequency,
      intervalCount: data?.interval_count,
      startDate: data?.start_date,
      endDate: data?.end_date,
      nextExecutionDate: data?.next_execution_date,
      lastExecutionDate: data?.last_execution_date,
      status: data?.status,
      emailNotification: data?.email_notification,
      monthlyOverflowRule: data?.monthly_overflow_rule,
      semiMonthlyRule: data?.semi_monthly_rule,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  }
};